import { CartItem, Product } from '../domain/types';

export type ShippingMethodType = 'local' | 'national' | 'pickup' | null;

export interface GuestInfo {
    name: string;
    email: string;
    dni: string;
    phone: string;
}

export interface CartState {
    // Core cart items
    items: CartItem[];
    // Sync state
    isSyncing: boolean;
    // Shipping & Checkout
    shippingCost: number;
    shippingMethod: ShippingMethodType;
    zipCode: string;
    guestInfo: GuestInfo | null;
    // Pricing
    couponCode: string | null;
    discountAmount: number;
}

export type CartAction =
    | { type: 'SET_ITEMS'; payload: CartItem[] }
    | { type: 'ADD_ITEM'; payload: { product: Product; quantity: number } }
    | { type: 'REMOVE_ITEM'; payload: string }
    | { type: 'DECREASE_QUANTITY'; payload: string }
    | { type: 'CLEAR_CART' }
    | { type: 'SET_SYNCING'; payload: boolean }
    | { type: 'SET_SHIPPING_COST'; payload: number }
    | { type: 'SET_SHIPPING_METHOD'; payload: ShippingMethodType }
    | { type: 'SET_ZIP_CODE'; payload: string }
    | { type: 'SET_GUEST_INFO'; payload: GuestInfo | null }
    | { type: 'APPLY_COUPON'; payload: string }
    | { type: 'REMOVE_COUPON' }
    | { type: 'SET_DISCOUNT_AMOUNT'; payload: number };

export const initialCartState: CartState = {
    items: [],
    isSyncing: false,
    shippingCost: 0,
    shippingMethod: null,
    zipCode: '',
    guestInfo: null,
    couponCode: null,
    discountAmount: 0
};

export function cartReducer(state: CartState, action: CartAction): CartState {
    switch (action.type) {
        case 'SET_ITEMS':
            return { ...state, items: action.payload };

        case 'ADD_ITEM': {
            const { product, quantity } = action.payload;
            const existingIndex = state.items.findIndex(item => item.product.id === product.id);
            
            if (existingIndex >= 0) {
                const newItems = [...state.items];
                newItems[existingIndex] = {
                    ...newItems[existingIndex],
                    quantity: newItems[existingIndex].quantity + quantity
                };
                return { ...state, items: newItems };
            }
            
            return {
                ...state,
                items: [...state.items, { product, quantity }]
            };
        }

        case 'REMOVE_ITEM':
            return {
                ...state,
                items: state.items.filter(item => item.product.id !== action.payload)
            };

        case 'DECREASE_QUANTITY': {
            const existing = state.items.find(item => item.product.id === action.payload);
            if (!existing) return state;

            if (existing.quantity > 1) {
                return {
                    ...state,
                    items: state.items.map(item =>
                        item.product.id === action.payload
                            ? { ...item, quantity: item.quantity - 1 }
                            : item
                    )
                };
            }
            
            return {
                ...state,
                items: state.items.filter(item => item.product.id !== action.payload)
            };
        }

        case 'CLEAR_CART':
            return {
                ...state,
                items: [],
                shippingCost: 0,
                shippingMethod: null,
                zipCode: '',
                guestInfo: null,
                couponCode: null,
                discountAmount: 0
            };

        case 'SET_SYNCING':
            return { ...state, isSyncing: action.payload };

        case 'SET_SHIPPING_COST':
            return { ...state, shippingCost: action.payload };

        case 'SET_SHIPPING_METHOD':
            return { ...state, shippingMethod: action.payload };

        case 'SET_ZIP_CODE':
            return { ...state, zipCode: action.payload };

        case 'SET_GUEST_INFO':
            return { ...state, guestInfo: action.payload };

        case 'APPLY_COUPON':
            return { ...state, couponCode: action.payload };

        case 'REMOVE_COUPON':
            return { ...state, couponCode: null, discountAmount: 0 };

        case 'SET_DISCOUNT_AMOUNT':
            return { ...state, discountAmount: action.payload };

        default:
            return state;
    }
}
